import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, Search, Eye, Edit, CheckCircle, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface Store {
  id: string;
  store_name: string;
  florist_id: string;
  store_status: 'active' | 'inactive' | 'pending';
  approval_status: 'approved' | 'rejected' | 'pending';
  created_at: string;
  suburb: string;
  state: string;
  delivery_radius: number;
}

export default function StoreManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAdmin, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) {
      return;
    }
    if (!user || !isAdmin) {
      navigate("/");
    }
  }, [user, isAdmin, authLoading, navigate]);

  const { data: stores, isLoading } = useQuery({
    queryKey: ['admin-stores'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Store[];
    },
    enabled: !authLoading && !!user && !!isAdmin
  });

  const approveStore = useMutation({
    mutationFn: async (storeId: string) => {
      if (!user?.id) throw new Error("User ID not found");
      
      try {
        // Try using the RPC function first
        const { data, error } = await supabase.rpc('approve_store', {
          store_id: storeId,
          admin_id: user.id
        });
        
        if (error) throw error;
        return data;
      } catch (functionError) {
        console.error("Error calling approve_store function:", functionError);
        
        // Fall back to direct update if the function isn't working
        const { data, error } = await supabase
          .from('store_profiles')
          .update({
            approval_status: 'approved',
            store_status: 'active',
            approved_at: new Date().toISOString(),
            approved_by: user.id,
            updated_at: new Date().toISOString()
          })
          .eq('id', storeId);
          
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
      toast.success("Store approved successfully");
    },
    onError: (error) => {
      console.error("Error approving store:", error);
      toast.error("Failed to approve store: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  });

  const setStoreToPendingMutation = useMutation({
    mutationFn: async (storeId: string) => {
      const { error } = await supabase
        .from('store_profiles')
        .update({ approval_status: 'pending', store_status: 'pending', updated_at: new Date().toISOString() })
        .eq('id', storeId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-stores'] });
      toast.success("Store status set to pending.");
    },
    onError: (error) => {
      console.error("Error setting store to pending:", error);
      toast.error("Failed to update store status.");
    }
  });

  const handleApproveStore = async (storeId: string) => {
    try {
      await approveStore.mutateAsync(storeId);
    } catch (error) {
      console.error("Error in handleApproveStore:", error);
    }
  };

  const handleSetStoreToPending = async (storeId: string) => {
    try {
      await setStoreToPendingMutation.mutateAsync(storeId);
    } catch (error) {
      console.error("Error in handleSetStoreToPending:", error);
    }
  };

  const filteredStores = stores?.filter(store => 
    store.store_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.suburb?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.state?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewStore = (storeId: string) => {
    navigate(`/admin/stores/${storeId}`);
  };

  const handleEditStore = (storeId: string) => {
    navigate(`/admin/stores/${storeId}/edit`);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-[#4A4F41]">Store Management</h1>
              <p className="text-muted-foreground">Manage and monitor all florist stores</p>
            </div>
            <div className="w-full md:w-auto mt-4 md:mt-0">
              <div className="relative w-full md:w-[260px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search stores..."
                  className="pl-8 bg-white border-gray-200"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
            </div>
          ) : (
            <div className="border rounded-lg bg-white shadow-sm">
              <Table>
                <TableHeader className="bg-[#F9F8F6]">
                  <TableRow>
                    <TableHead>Store Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Delivery Radius</TableHead>
                    <TableHead>Store Status</TableHead>
                    <TableHead>Approval Status</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStores?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No stores found matching your search
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredStores?.map((store) => (
                    <TableRow key={store.id}>
                      <TableCell className="font-medium">
                        {store.store_name}
                      </TableCell>
                      <TableCell>
                        {store.suburb}, {store.state}
                      </TableCell>
                      <TableCell>
                        {store.delivery_radius}km
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(store.store_status)}>
                          {store.store_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusBadgeColor(store.approval_status)}>
                          {store.approval_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {format(new Date(store.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {store.approval_status === 'pending' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApproveStore(store.id)}
                              title="Approve Store"
                              className="text-green-600 hover:bg-green-50 hover:text-green-700 px-2 py-1 h-auto"
                              disabled={approveStore.isPending && approveStore.variables === store.id}
                            >
                              {approveStore.isPending && approveStore.variables === store.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <CheckCircle className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1">Approve</span>
                            </Button>
                          )}
                          {store.approval_status === 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetStoreToPending(store.id)}
                              title="Set to Pending (Unlist)"
                              className="text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700 px-2 py-1 h-auto"
                              disabled={setStoreToPendingMutation.isPending && setStoreToPendingMutation.variables === store.id}
                            >
                              {setStoreToPendingMutation.isPending && setStoreToPendingMutation.variables === store.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                <XCircle className="h-3.5 w-3.5" />
                              )}
                              <span className="ml-1">Unlist</span>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewStore(store.id)}
                            title="View Store"
                            className="hover:bg-[#967F81]/10 hover:text-brand-primary"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditStore(store.id)}
                            title="Edit Store"
                            className="hover:bg-[#967F81]/10 hover:text-brand-primary"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
} 